import { useQuery } from '@tanstack/react-query';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';

import { cefrApi } from '@/api/cefr';
import { materialsApi } from '@/api/materials';
import { useHasFeature } from '@/hooks/useHasFeature';
import { cn } from '@/lib/utils';

import { StudentSideWidgets } from './StudentSideWidgets';

/** Навыки в том порядке, что в макете. */
const SKILLS = ['reading', 'listening', 'speaking', 'writing'] as const;

/**
 * Правая колонка экрана «Учёба» (макет «Desktop Web v2»): куратор, уровни по
 * навыкам, ближайшее занятие и переход в материалы.
 *
 * Карточка показывается, только когда данные для неё есть: пустая рамка с
 * заголовком читается как поломка, а придуманные значения — хуже пустоты.
 */
export function LearnSidebar({ marathonIds }: { marathonIds: string[] }) {
  const { t } = useTranslation();

  // ⚠️ БЕЗ ДОСТУПА ЗАПРОС НЕ ШЛЁМ (19.09.2026, договор `selling-words-guard.md` §8.3): карточка уровней
  // и так показывается только измеренным, а отказ по возможности — лишний 403 в трафике (Apple 3.1.1).
  const hasCefr = useHasFeature('cefr');
  const skills = useQuery({
    queryKey: ['student', 'cefr-skills'],
    queryFn: cefrApi.getSkills,
    enabled: hasCefr,
  });
  const materials = useQuery({ queryKey: ['student', 'materials'], queryFn: materialsApi.listForStudent });

  /**
   * Уровни показываем, только когда их ИЗМЕРИЛИ.
   *
   * Пустая карточка «Reading A1, Listening A1, Speaking A1, Writing A1» с
   * нулевыми полосками — это тот самый выдуманный уровень, который мы убрали
   * с главной: до мока у ученика уровня нет, а не «A1».
   */
  const hasMeasuredSkills = !!skills.data && SKILLS.some((s) => (skills.data![s]?.score ?? 0) > 0);

  return (
    <div className="space-y-3">
      {/* ⚠️ КУРАТОР, СЛОВО ДНЯ, КАЛЕНДАРЬ И ЗАДАНИЯ — ОБЩИЕ С ДРУГИМИ
          РАЗДЕЛАМИ. Раньше они жили здесь копией; 07.09.2026, когда те же
          виджеты понадобились в тренажёрах, копии развели бы правки по разным
          файлам.

          ⚠️ ПОРЯДОК НА ЭКРАНЕ СОХРАНЁН В ТОЧНОСТИ. Уровни по навыкам стоят
          вторыми, сразу за куратором, — как и до выноса. Отдать их «как
          получится» значило бы переставить рабочий экран заодно с переносом
          кода, о чём никто не просил. */}
      <StudentSideWidgets
        marathonIds={marathonIds}
        afterCurator={
          <>
        {/* ── Уровни по навыкам ───────────────────────────────────────────── */}
        {hasMeasuredSkills && (
          <div className="rounded-[20px] bg-surface-container px-[18px] py-4">
            <h3 className="mb-3 text-[13.5px] font-bold text-on-surface">{t('learn.skillsCefr')}</h3>
            <div className="flex flex-col gap-2.5">
              {SKILLS.map((skill) => {
                const s = skills.data![skill];
                // Цвет по уровню, как в макете: начальные уровни синие,
                // от B1 — фирменный оранжевый. Правило одно для метки и полоски.
                const strong = /^[BC]/i.test(s.level);
                return (
                  <div key={skill}>
                    <div className="flex items-center gap-3">
                      <span className="flex-1 text-[12.5px] font-semibold text-on-surface">
                        {t(`trainersHub.ielts${skill[0].toUpperCase()}${skill.slice(1)}`)}
                      </span>
                      <span
                        className={cn(
                          'rounded-full px-2 py-0.5 text-[10px] font-bold',
                          strong ? 'bg-primary-container text-on-primary-container' : 'bg-info-container text-info',
                        )}
                      >
                        {s.level}
                      </span>
                    </div>
                    <div className="mt-[5px] h-[5px] rounded-full bg-outline">
                      <div
                        className={cn('h-[5px] rounded-full transition-[width] duration-500', strong ? 'bg-primary' : 'bg-info')}
                        style={{ width: `${Math.min(100, Math.max(0, s.score))}%` }}
                      />
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        )}
          </>
        }
      />

      {/* ── Материалы ───────────────────────────────────────────────────── */}
      {!!materials.data?.length && (
        <Link
          to="/app/materials"
          className="flex items-center gap-3 rounded-[20px] bg-info-container px-[18px] py-[15px] transition hover:brightness-[0.97]"
        >
          <span className="grid h-9 w-9 flex-none place-items-center rounded-xl bg-white">
            <span className="material-symbols-outlined text-[18px] text-info">smart_display</span>
          </span>
          <span className="min-w-0 flex-1">
            <span className="block text-[13.5px] font-semibold text-on-surface">{t('materials.title')}</span>
            <span className="block text-[11.5px] text-on-surface-variant">
              {`${t('learn.videosCount', { count: materials.data.filter((m) => m.type === 'video').length })} · ${t('learn.articlesCount', { count: materials.data.filter((m) => m.type === 'article').length })}`}
            </span>
          </span>
          <span className="material-symbols-outlined flex-none text-[16px] text-info">chevron_right</span>
        </Link>
      )}
    </div>
  );
}
